
<script setup>
import {
    computed,
    inject,
    ref,
    watch
} from 'vue';

import { useRoute } from 'vue-router';
import { useCheckPermission } from '@/composables/useCheckPermission';

const props = defineProps({
    item: {
        type: Object,
        required: true
    }
});

const route = useRoute();
const expandSidebar = inject('expandSidebar', ref(false));

const tienePermiso = computed(() =>
    useCheckPermission(props.item.permission).hasPermission === true
);

const tieneDropdown = computed(() =>
    Array.isArray(props.item.dropdown) &&
    props.item.dropdown.length > 0
);

const rutaActiva = computed(() =>
    route.matched.some(r => r.name === props.item.name)
);

const contieneRutaActiva = (item) => {
    if (route.matched.some(r => r.name === item.name)) {
        return true;
    }

    if (!Array.isArray(item.dropdown)) {
        return false;
    }

    return item.dropdown.some(contieneRutaActiva);
};

const submenuActivo = computed(() =>
    contieneRutaActiva(props.item)
);

const abierto = ref(submenuActivo.value);

// ----------------------------------------------------
// EVENTOS
// ----------------------------------------------------

const expandirSidebar = () => {
    expandSidebar.value = true;
};

const toggleSubmenu = () => {
    expandSidebar.value = true;
    abierto.value = !abierto.value;
};

// ----------------------------------------------------
// WATCHERS
// ----------------------------------------------------

watch(
    () => route.fullPath,
    () => {
        abierto.value = submenuActivo.value;
    }
);

watch(expandSidebar, (expandido) => {
    if (expandido && submenuActivo.value) {
        abierto.value = true;
    }
});
</script>

<template>
    <li
        v-if="tienePermiso"
        class="sidebar-item"
    >
        <!-- ENLACE INTERNO SIN SUBMENÚ -->
        <router-link
            v-if="!tieneDropdown && item.new === true"
            :to="{ name: item.name }"
            class="sidebar-link link-body-emphasis"
            :class="'submenu' + item.level"
            @click="expandirSidebar"
        >
            <i :class="item.iconClass" class="fa-fw me-1"></i>
            <span>{{ item.label }}</span>
        </router-link>

        <!-- ENLACE EXTERNO O TRADICIONAL -->
        <a
            v-else-if="!tieneDropdown && item.new === false"
            :href="`/${item.link}`"
            class="sidebar-link link-body-emphasis"
            :class="'submenu' + item.level"
            @click="expandirSidebar"
        >
            <i :class="item.iconClass" class="fa-fw me-1"></i>
            <span>{{ item.label }}</span>
        </a>

        <!-- ELEMENTO CON SUBMENÚ -->
        <template v-else-if="tieneDropdown">
            <button
                type="button"
                class="sidebar-link link-body-emphasis sidebar-dropdown-toggle"
                :class="[
                    'submenu' + item.level,
                    {
                        'collapsed': !abierto,
                        'sidebar-link-active': rutaActiva
                    }
                ]"
                :aria-expanded="abierto"
                @click="toggleSubmenu"
            >
                <i :class="item.iconClass" class="fa-fw me-1"></i>
                <span>{{ item.label }}</span>

                <i
                    class="fa-solid fa-chevron-down sidebar-chevron"
                    :class="{ 'sidebar-chevron-open': abierto }"
                ></i>
            </button>

            <ul
                v-show="abierto && expandSidebar"
                class="sidebar-dropdown list-unstyled"
            >
                <SidebarItem
                    v-for="(subItem, index) in item.dropdown"
                    :key="subItem.name ?? subItem.link ?? index"
                    :item="subItem"
                />
            </ul>
        </template>
    </li>
</template>

<style scoped>
/* ----------------------------------------------------
   ENLACES ACTIVOS
---------------------------------------------------- */

.router-link-active,
.sidebar-link-active {
    background-color: #dc3545 !important;
    border-left: 3px solid #7f2929 !important;
}

.sidebar-dropdown .router-link-active {
    background-color: transparent !important;
}

.sidebar-dropdown .router-link-exact-active {
    color: #3a762f !important;
    background-color: #dc3545 !important;
}

/* ----------------------------------------------------
   BOTÓN DROPDOWN
---------------------------------------------------- */

.sidebar-dropdown-toggle {
    position: relative;
    width: 100%;

    display: flex;
    align-items: center;

    border: 0;
    border-left: 3px solid transparent;

    background: transparent;
    color: inherit;

    text-align: left;
    cursor: pointer;
}

.sidebar-dropdown-toggle:hover {
    background-color: var(--bs-danger-bg-subtle);
}

.sidebar-chevron {
    margin-left: auto;
    font-size: 0.7rem;
    transition: transform 0.2s ease;
}

.sidebar-chevron-open {
    transform: rotate(180deg);
}

/* ----------------------------------------------------
   SUBMENÚS
---------------------------------------------------- */

.sidebar-dropdown {
    margin: 0;
    padding: 0;
}

.sidebar-icon {
    text-align: center;
    width: 100%;
}
</style>
